import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/consuntivo";
import { Calculator, RefreshCw } from "lucide-react";

const eur = (n) => { const [i, d] = (n || 0).toFixed(2).split("."); return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`; };
const num = (v) => { const s = String(v ?? ""); return parseFloat(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s) || 0; };
const fmt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const hh = (n) => `${(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} h`;
const Card = ({ title, children, id }) => (
  <div className="rounded-2xl bg-[#181A22] border border-white/10 p-4" data-testid={id}>
    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">{title}</div>
    {children}
  </div>
);
const Row = ({ k, v, strong, id }) => <div className={`flex justify-between text-sm py-0.5 ${strong ? "font-bold text-base" : ""}`}><span className="text-muted-foreground">{k}</span><span data-testid={id}>{v}</span></div>;

export const BodyshopLive = ({ id, quote, onData, reloadKey }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/admin/bodyshop/${id}/consuntivo`).then((r) => setD(r.data)).catch(() => {}), [id, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!d || !onData) return;
    const net = Math.round(d.hours.total * (d.final.hourly_rate || 0) * 100) / 100 + (d.job.final ? d.final.materials_amount : d.hours.materials_eur || 0) + (d.final.parts_amount || 0);
    const saved = (d.final.materials || []).filter((m) => m.qty || m.price).map((m) => ({ ...m, qty: [m.qty, m.price && eur(money(m.price))].filter(Boolean).join(" · ") }));
    const materials = saved.length ? saved : Object.entries(d.hours.materials || {}).map(([k, v]) => ({ description: k[0].toUpperCase() + k.slice(1), qty: v }));
    onData({ hours: d.hours.total, total: net, operators: d.hours.operators, materials });
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [load]);

  if (!d) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  const { hours, final, job } = d;
  const rate = final.hourly_rate || 0;
  const labour = Math.round(hours.total * rate * 100) / 100;
  const mat = (job.final ? final.materials_amount : hours.materials_eur) || 0;
  const parts = final.parts_amount || 0;
  const net = labour + mat + parts;
  const vat = Math.round(net * 22) / 100;
  const q = num(quote);
  const diff = net - q;
  const mats = Object.entries(hours.materials || {});

  return (
    <div className="space-y-3" data-testid="bodyshop-live">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold uppercase text-sm">{job.status === "closed" ? "Lavoro chiuso" : "Lavorazione in tempo reale"}</h2>
        <button onClick={load} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Aggiorna" data-testid="bodyshop-live-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <Card title="Preventivo vs consuntivo" id="live-totals">
        <Row k="Preventivo (+IVA)" v={q ? eur(q) : "—"} id="live-quote" />
        <Row k={`Manodopera ${hh(hours.total)} × ${eur(rate)}`} v={eur(labour)} id="live-labour" />
        <Row k="Materiali" v={eur(mat)} id="live-materials-amount" />
        <Row k="Ricambi" v={eur(parts)} id="live-parts-amount" />
        <Row k="Consuntivo (imponibile)" v={eur(net)} strong id="live-net" />
        <Row k="Totale con IVA 22%" v={eur(net + vat)} id="live-total" />
        {q > 0 && <div className={`mt-2 rounded-lg px-3 py-1.5 text-sm font-semibold ${diff > 0 ? "bg-[#E63946]/15 text-[#FF6B6B]" : "bg-[#16A34A]/15 text-[#4ADE80]"}`} data-testid="live-diff">
          {diff > 0 ? `Oltre il preventivo di ${eur(diff)}` : `Entro il preventivo, margine ${eur(-diff)}`}
        </div>}
      </Card>
      <Card title={`Ore lavorate · ${hh(hours.total)}`} id="live-hours">
        {hours.operators.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna scheda ore caricata per la targa {job.plate || "—"}.</p> : (
          <>
            {hours.operators.map((o) => <Row key={o.name} k={o.name} v={hh(o.hours)} />)}
            <div className="mt-2 pt-2 border-t border-white/10 space-y-1 max-h-48 overflow-y-auto">
              {hours.entries.slice().reverse().map((e, i) => (
                <div key={i} className="text-xs grid grid-cols-[72px_1fr_auto] gap-2"><span className="text-muted-foreground">{fmt(e.date)}</span><span className="truncate">{e.operator}{e.works ? ` · ${e.works}` : ""}</span><span className="font-semibold">{hh(e.hours)}</span></div>
              ))}
            </div>
          </>
        )}
      </Card>
      {mats.length > 0 && (
        <Card title="Materiali dalle schede operatore" id="live-materials">
          {mats.map(([k, v]) => <Row key={k} k={k[0].toUpperCase() + k.slice(1)} v={hours.materials_cost?.[k] && !/^[\d.,]+ €$/.test(v) ? `${v} = ${eur(hours.materials_cost[k])}` : v} id={`live-material-${k}`} />)}
        </Card>
      )}
      <Link to={`/admin/carrozzeria/${id}/consuntivo`} className="flex items-center justify-center gap-2 rounded-full border border-[#16A34A]/50 text-[#4ADE80] py-2.5 text-sm font-semibold hover:bg-[#16A34A]/10" data-testid="live-open-consuntivo">
        <Calculator className="w-4 h-4" />{job.status === "closed" ? "Apri consuntivo" : "Tariffa, materiali e fine lavoro"}
      </Link>
    </div>
  );
};
